import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { getApiBaseUrl } from './apiConfig';

let stripePromiseCache = null;
let stripeKeyCache = null;

/**
 * Fetch Stripe publishable key from backend API
 * This ensures the key is available at runtime, not just build time
 */
const fetchStripePublishableKey = async () => {
  try {
    // Check if we already have the key cached
    if (stripeKeyCache) {
      return stripeKeyCache;
    }

    // Try to get from environment variable first (for backward compatibility)
    const envKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (envKey && envKey !== 'undefined' && envKey !== 'NOT SET') {
      stripeKeyCache = envKey;
      return envKey;
    }

    // Fetch from backend API
    console.log('Fetching Stripe key from backend API...');
    const apiUrl = getApiBaseUrl();
    const response = await axios.get(`${apiUrl}/config`);

    if (response.data?.success && response.data?.data?.stripe?.publishableKey) {
      const key = response.data.data.stripe.publishableKey;

      // Validate the key format
      if (key === 'NOT SET' || !key.startsWith('pk_')) {
        throw new Error('Invalid Stripe publishable key received from server');
      }

      stripeKeyCache = key;
      console.log('Stripe key loaded successfully:', key.substring(0, 15) + '...');
      return key;
    }

    throw new Error('Failed to fetch Stripe publishable key from server');
  } catch (error) {
    console.error('Error fetching Stripe key:', error);
    throw new Error(`Failed to initialize Stripe: ${error.message}`);
  }
};

/**
 * Load Stripe instance with publishable key from backend
 * Returns a promise that resolves to the Stripe instance
 */
export const getStripePromise = async () => {
  try {
    // Return cached promise if available
    if (stripePromiseCache) {
      return stripePromiseCache;
    }

    // Fetch the key and load Stripe
    const publishableKey = await fetchStripePublishableKey();
    stripePromiseCache = loadStripe(publishableKey);

    return stripePromiseCache;
  } catch (error) {
    console.error('Error loading Stripe:', error);
    // Reset cache on error so it can be retried
    stripePromiseCache = null;
    stripeKeyCache = null;
    throw error;
  }
};

/**
 * Get Stripe publishable key only (without loading Stripe instance)
 * Useful for verification or logging
 */
export const getStripePublishableKey = async () => {
  return fetchStripePublishableKey();
};

/**
 * Clear cached Stripe instance and key
 * Useful for testing or when switching between environments
 */
export const clearStripeCache = () => {
  stripePromiseCache = null;
  stripeKeyCache = null;
};

export default getStripePromise;
